/**
 * `@aio/platform-form` — public barrel.
 *
 * The public surface is the high-level `<Form>` composer (added in a
 * later phase) plus the field-definition primitives, the form-state
 * hook, and the form context that field components consume.
 */

// ─── Field type definitions ────────────────────────────────────────────────
export {
  defineFields,
  type FieldType,
  type FieldOption,
  type FormValues,
  type BaseFieldDef,
  type TextFieldDef,
  type TextareaFieldDef,
  type NumberFieldDef,
  type MoneyFieldDef,
  type PercentFieldDef,
  type DateFieldDef,
  type SelectFieldDef,
  type MultiSelectFieldDef,
  type CheckboxFieldDef,
  type SwitchFieldDef,
  type RadioFieldDef,
  type TagsFieldDef,
  type FileFieldDef,
  type RepeaterFieldDef,
  type RelationFieldDef,
  type FieldDef,
} from "./types";

// ─── Form state hook ───────────────────────────────────────────────────────
export {
  useForm,
  readFormErrorMessage,
  FORM_ERROR_KEY,
  type UseFormOptions,
  type UseFormResult,
} from "./use-form";

// ─── Form context ──────────────────────────────────────────────────────────
export {
  FormProvider,
  useFormContext,
  type FormContextValue,
  type FormProviderProps,
} from "./form-context";

// ─── Field components ──────────────────────────────────────────────────────
export { TextField, type TextFieldProps } from "./fields/text-field";
export { TextareaField, type TextareaFieldProps } from "./fields/textarea-field";
export { NumberField, type NumberFieldProps } from "./fields/number-field";
export { CheckboxField, type CheckboxFieldProps } from "./fields/checkbox-field";
export { SwitchField, type SwitchFieldProps } from "./fields/switch-field";
export { RadioField, type RadioFieldProps } from "./fields/radio-field";
export { MoneyField, type MoneyFieldProps } from "./fields/money-field";
export { PercentField, type PercentFieldProps } from "./fields/percent-field";
export { DateField, type DateFieldProps } from "./fields/date-field";
export { SelectField, type SelectFieldProps } from "./fields/select-field";
export {
  MultiSelectField,
  type MultiSelectFieldProps,
} from "./fields/multi-select-field";
export { TagsField, type TagsFieldProps } from "./fields/tags-field";
export { FileField, formatBytes, type FileFieldProps } from "./fields/file-field";
export { RepeaterField, type RepeaterFieldProps } from "./fields/repeater-field";
export { RelationField, type RelationFieldProps } from "./fields/relation-field";

// ─── Field renderer dispatcher ─────────────────────────────────────────────
export { FieldRenderer, type FieldRendererProps } from "./field-renderer";

// ─── Conditional visibility helper ─────────────────────────────────────────
export { evaluateCondition } from "./conditional";

// ─── Form-level error banner ───────────────────────────────────────────────
export {
  FormErrorBanner,
  type FormErrorBannerProps,
} from "./form-error-banner";

// ─── Dirty-confirm guard ───────────────────────────────────────────────────
export {
  useDirtyConfirm,
  type UseDirtyConfirmOptions,
} from "./dirty-confirm";

// ─── Autosave ──────────────────────────────────────────────────────────────
export {
  useAutosave,
  autosaveStorageKey,
  type UseAutosaveOptions,
  type UseAutosaveResult,
} from "./autosave";

// ─── Keyboard shortcuts ────────────────────────────────────────────────────
export { useFormKeyboard, type UseFormKeyboardOptions } from "./keyboard";

// ─── Public composer ───────────────────────────────────────────────────────
export { Form, type FormProps } from "./form";
